/* eslint react/display-name: 0 */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import R from 'ramda'
import moment from 'moment'
import { getLicensePoolTypeLabel } from '_utils/licensePools';

import {
  PAGES
} from '_constants'

import {
  Accordion,
  Button,
  Divider,
  Grid,
  Segment
} from 'semantic-ui-react'

import LineItem from '_views/lineItem'
import UserInfo from '_views/userInfo'
import SerialInfo from '_views/serialInfo'
import useCanAdminLicenses from '../../../hooks/useCanAdminLicenses';
import AssignSerialForm from '_forms/serialAssign';

const getProductType = R.propOr('unkown', 'type')
const getProductName = R.propOr('unkown', 'name')
const getProductExpires = R.prop('expires_on')
const getProductExpiresLabel = R.compose(
  R.ifElse(
    x => moment(x).toDate() > new Date(),
    R.always('expires'),
    R.always(<u>expired</u>)
  ),
  R.propOr('', 'expires_on')
)

const ModifySerialPanel = ({
  canAdmin,
  onModify
}) => {

  const [showAssignSerialForm, setShowAssignSerialForm] = useState(false);
  const [values, setValues] = useState({ });
  const [isDirty, setIsDirty] = useState(false);

  const onCancel = () => setShowAssignSerialForm(false);
  const onValueChange = (e, { name, value }) => {

    if (name === "type") {
      setValues({ type: value });
    } else {
      setValues(Object.assign({}, values, { [name]: value }));
    }
    setIsDirty(true);
  };
  const onShowForm = () => {
    setShowAssignSerialForm(true);
    setValues({ type: 'license' });
  };

  const onSubmit = () => {
    const field = values.type === 'license' ? 'license_id' : 'license_pool_id';
    const value = values[field];
    if (!value || !value.length) {
      return void window.alert('Missing ID');
    }

    onModify({ type: values.type, [field]: value });
    setShowAssignSerialForm(false);
  };

  return (
    <div>
      {
        showAssignSerialForm ? (
          <AssignSerialForm
            onCancel={ onCancel }
            onChange={ onValueChange }
            onSubmit={ onSubmit }
            serial={ values }
            dirty={ isDirty }
          />
        ) : (
          <React.Fragment>
            <p>
              This serial number has so far not been used for an upgrade.
            </p>
            {
              canAdmin ? (
                <Button onClick={ onShowForm } >Assign License</Button>
              ) : null
            }
          </React.Fragment>
        )
      }
    </div>
  );
};

ModifySerialPanel.propTypes = {
  canAdmin: PropTypes.bool.isRequired,
  onModify: PropTypes.func.isRequired
};

const getPanels = ({ canAdmin, serial, setNavigation, onModifySerial, onUnlinkSerial }) => {
  return [
    !serial.license && !serial.license_pool ? {
      title: "Not Upgraded",
      content: {
        content: <ModifySerialPanel canAdmin={ canAdmin } onModify={ onModifySerial } />,
        key: `serial_${serial._id}_noup`
      }
    } : null,

    serial.license && serial.license.user ? {
      title: "User",
      content: {
        content: (
          <div>
            <UserInfo
              user={ serial.license.user }
              onClick={ () => setNavigation({ pageName: PAGES.USERS, ids: [serial.license.user._id] }) }
            />
          </div>
        ),
        key: `serial_${serial._id}_user`
      }
    } : null,
    serial.license ? {
      title: "License",
      content: {
        content: (
          <div>
            <LineItem label='License ID' text={ serial.license._id } onClick={ () => setNavigation({ pageName: PAGES.LICENSES, ids: [serial.license._id] }) } />
            <LineItem label={ getProductName(serial.license) } text={ getProductType(serial.license) } />
            <LineItem label={ getProductExpiresLabel(serial.license) } text={ getProductExpires(serial.license) } isDate={ true } />
            {
              canAdmin ? (
                <React.Fragment>
                  <Divider />
                  <Button size="mini" negative onClick={ onUnlinkSerial } >Unlink License</Button>
                </React.Fragment>
              ) : null
            }
          </div>
        ),
        key: `serial_${serial._id}_lic`
      }
    } : null,
    serial.license_pool && serial.license_pool.institution ? {
      title: "Institution",
      content: {
        content: (
          <div>
            <LineItem label='Name' text={ serial.license_pool.institution.name } onClick={ () => setNavigation({ pageName: PAGES.INSTITUTIONS, ids: [serial.license_pool.institution._id] }) } />
            <LineItem label='Status' text={ serial.license_pool.institution.status } />
            <LineItem label='ID' text={ serial.license_pool.institution._id } />
          </div>
        ),
        key: `serial_${serial._id}_inst`
      }
    } : null,
    serial.license_pool ? {
      title: "License Pool",
      content: {
        content: (
          <div>
            <LineItem label="Pool Type" text={ getLicensePoolTypeLabel(serial.license_pool) } />
            <LineItem label='Pool ID' text={ serial.license_pool._id } />
            {
              canAdmin ? (
                <React.Fragment>
                  <Divider />
                  <Button size="mini" negative onClick={ onUnlinkSerial } >Unlink License Pool</Button>
                </React.Fragment>
              ) : null
            }
          </div>
        ),
        key: `serial_${serial._id}_pool`
      }
    } : null
  ]
    .filter(p => p)
};

// this class uses static class properties for auto-binding
// https://babeljs.io/docs/plugins/transform-class-properties/
// https://github.com/tc39/proposal-private-fields
const SerialItem = ({
  serial,
  setNavigation,
  onModifySerial,
  onUnlinkSerial
}) => {

  const canAdmin = useCanAdminLicenses();
  const panels = getPanels({
    canAdmin,
    serial,
    setNavigation,
    onModifySerial: (payload) => onModifySerial({
      serialId: serial._id,
      payload
    }),
    onUnlinkSerial: () => {
      const msg = serial.license ? `the license ${serial.license._id}` : `the license pool ${serial.license_pool._id}`;
      if (window.confirm(`Are you sure that you want to unlink the serial with id ${serial._id} from ${msg}? This action cannot be reverted.`)) {
        onUnlinkSerial({ serialId: serial._id });
      }
    }
  });

  return (
    <Segment>
      <Grid columns={ 2 } >
        <Grid.Row>
          <Grid.Column width={ 8 }>
            <SerialInfo serial={ serial } />
          </Grid.Column>
          <Grid.Column width={ 8 } >
            <Accordion
              defaultActiveIndex={0}
              panels={panels}
              styled
              fluid
              exclusive={false}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

SerialItem.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  serial: PropTypes.object.isRequired,

  setNavigation: PropTypes.func.isRequired,

  onRefreshSerial: PropTypes.func.isRequired,
  onModifySerial: PropTypes.func.isRequired,
  onUnlinkSerial: PropTypes.func.isRequired
};

export default SerialItem;



// WEBPACK FOOTER //
// ./webAdmin/components/serials/serial/index.js